import React, { useState } from "react";
import { useVariants } from "../../hooks/useVariants";
import { TableStatus } from "../../components/TableStatus";
import ConfirmationModal from "../../common/ConfirmationModal";

export default function VariantsPage() {
    const { 
        variants, 
        isLoading, 
        isError, 
        error, 
        createVariant, 
        updateVariant, 
        deleteVariant, 
        isSaving, 
        isDeleting 
    } = useVariants();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);
    const [variantToDelete, setVariantToDelete] = useState(null);
    const [variantName, setVariantName] = useState("");

    const handleOpenModal = (variant = null) => {
        if (variant) {
            setEditingVariant(variant);
            setVariantName(variant.name);
        } else {
            setEditingVariant(null);
            setVariantName("");
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingVariant) {
                await updateVariant({ id: editingVariant._id, data: { name: variantName } });
            } else {
                await createVariant({ name: variantName });
            }
            setIsModalOpen(false);
        } catch (err) {
            console.error("Failed to save variant", err);
        }
    };

    const handleDelete = async () => {
        if (!variantToDelete) return;
        await deleteVariant(variantToDelete._id);
        setVariantToDelete(null);
    };

    return (
        <>
            <div className="card shadow-sm">
                {/* Header - Matching Course Page */}
                <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                    <h4 className="mb-0 text-primary-emphasis">Variant Management</h4>
                    <button className="btn btn-success btn-sm" onClick={() => handleOpenModal()}>
                        <i className="fas fa-plus mr-1"></i> Add Variant
                    </button>
                </div>

                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: "80px" }}>No</th>
                                    <th>Variant Name</th>
                                    <th>Created At</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus
                                    status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                    error={error}
                                    dataLength={variants?.length}
                                    colSpan={4}
                                />

                                {!isLoading && variants?.map((item, index) => (
                                    <tr key={item._id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark">{item.name}</div>
                                        </td>
                                        <td className="text-muted">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary mr-2"
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setVariantToDelete(item)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal for Add/Edit */}
            {isModalOpen && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-light">
                                <h5 className="modal-title">
                                    {editingVariant ? "Edit Variant" : "Add New Variant"}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Variant Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Color, Size, Material"
                                            value={variantName}
                                            onChange={(e) => setVariantName(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                        {isSaving ? "Saving..." : "Save Variant"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal for Delete */}
            <ConfirmationModal
                show={variantToDelete !== null}
                onClose={() => setVariantToDelete(null)}
                onConfirm={handleDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                confirmButtonVariant="danger"
                isLoading={isDeleting}
            >
                Are you sure you want to delete the variant <strong>{variantToDelete?.name}</strong>?
            </ConfirmationModal>
        </>
    );
}